/**
 * Shared HTTP client for the general (PMS) repricing surfaces.
 *
 * Same-origin `fetch` with cookies, JSON in and out — the style the My
 * Business admin pages use. The server answers errors as
 * `{ error, code, details }` (400 schema · 403 feature · 404
 * missing-or-other-tenant · 409 conflict · 422 business rule), which
 * surface as `ApiError` so a screen can show `error` verbatim and branch
 * on `code`.
 *
 * Lives one level above `Setup/` and `Page/` because both consume it:
 * Setup since CU-869f043ba, the Strategies wizard since CU-869f043c1.
 */

export class ApiError extends Error {
	constructor(status, body) {
		super(body?.error || `HTTP ${status}`)
		this.name = 'ApiError'
		this.status = status
		this.code = body?.code || 'http_error'
		this.details = body?.details || null
		// the field and the ids a 422 names (`/api/user/identity`, CU-869fknfmk)
		this.field = body?.field ?? null
		this.ids = body?.ids ?? null
	}
}

export function makeClient(base) {
	async function request(method, path, body) {
		const res = await fetch(`${base}${path}`, {
			method,
			credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: body === undefined ? undefined : JSON.stringify(body),
		})
		if (res.status === 204) return null
		let json = null
		try { json = await res.json() } catch { /* non-JSON body */ }
		if (!res.ok) throw new ApiError(res.status, json)
		return json
	}

	return {
		get:    (path)       => request('GET', path),
		post:   (path, body) => request('POST', path, body),
		put:    (path, body) => request('PUT', path, body),
		delete: (path, body) => request('DELETE', path, body),
	}
}

/** One resource = one entity path; `list` unwraps the `{items}` envelope. */
export function resource(http, path) {
	return {
		list:   ()        => http.get(path).then(r => r.items),
		get:    (id)      => http.get(`${path}/${id}`),
		create: (dto)     => http.post(path, dto),
		update: (id, dto) => http.put(`${path}/${id}`, dto),
		remove: (id)      => http.delete(`${path}/${id}`),
	}
}
